// src/views/Reports/StudentReport.jsx
import { useEffect, useMemo, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";
import Table from "../../components/Tables/Table";
import TableHeader from "../../components/Tables/TableHeader";
import { Select } from "antd";
import toast from "react-hot-toast";
import { getStudentStatistics } from "../../services/studentStatisticsService";
import { getInstitutions } from "../../services/institutionService";
import { getSessions } from "../../services/sessionService";
import DownloadButton from "../../components/Buttons/DownloadButton";
import { generatePDF } from "../../utils/pdf";
import { utils, writeFile } from "xlsx";

const { Option } = Select;

const StudentReport = () => {
  const [stats, setStats] = useState([]);
  const [filteredStats, setFilteredStats] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [sessions, setSessions] = useState([]);

  const [selectedInstitution, setSelectedInstitution] =
    useState("Not Selected");
  const [selectedSession, setSelectedSession] = useState("Not Selected");
  const [searchTerm, setSearchTerm] = useState("");

  const [loading, setLoading] = useState(false);
  const [expandedRowKey, setExpandedRowKey] = useState(null);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [statsRes, instRes, sessRes] = await Promise.all([
        getStudentStatistics(),
        getInstitutions(),
        getSessions(),
      ]);
      const statData = statsRes?.data?.data || [];
      setStats(statData);
      setFilteredStats(statData);
      setInstitutions(instRes?.data?.data || []);
      setSessions(sessRes?.data?.data || []);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Filters
  useEffect(() => {
    let filtered = stats;

    if (searchTerm.trim() !== "") {
      filtered = filtered.filter((item) =>
        (item.institution?.name || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }

    if (selectedInstitution !== "Not Selected") {
      filtered = filtered.filter(
        (item) =>
          item.institution?.id?.toString() === selectedInstitution.toString()
      );
    }

    if (selectedSession !== "Not Selected") {
      filtered = filtered.filter(
        (item) => item.session?.id?.toString() === selectedSession.toString()
      );
    }

    setFilteredStats(filtered);
  }, [searchTerm, selectedInstitution, selectedSession, stats]);

  // Group by Institution + Session and compute totals
  const grouped = useMemo(() => {
    const map = new Map();
    for (const row of filteredStats) {
      const inst = row.institution;
      const sess = row.session;
      if (!inst || !sess) continue;

      const key = `${inst.id}__${sess.id}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          institution: inst,
          session: sess,
          data: [],
          totals: {
            humanities_male: 0, // number_of_males
            humanities_female: 0, // number_of_females
            sciences_male: 0, // sciences_number_of_males
            sciences_female: 0, // sciences_number_of_females
            special_needs: 0, // number_of_students_with_special_needs
            total: 0, // humanities + sciences (no special needs)
          },
        });
      }
      const g = map.get(key);
      g.data.push(row);

      const humM = row.number_of_males || 0;
      const humF = row.number_of_females || 0;
      const sciM = row.sciences_number_of_males || 0;
      const sciF = row.sciences_number_of_females || 0;
      const spN = row.number_of_students_with_special_needs || 0;

      g.totals.humanities_male += humM;
      g.totals.humanities_female += humF;
      g.totals.sciences_male += sciM;
      g.totals.sciences_female += sciF;
      g.totals.special_needs += spN;
      g.totals.total += humM + humF + sciM + sciF;
    }
    return Array.from(map.values());
  }, [filteredStats]);

  const columns = [
    {
      title: "Institution",
      dataIndex: "institution",
      render: (institution) => (
        <span className="font-medium text-gray-800">{institution?.name}</span>
      ),
    },
    {
      title: "Year",
      dataIndex: ["session", "name"],
    },
    {
      title: "Male (Humanities)",
      dataIndex: ["totals", "humanities_male"],
    },
    {
      title: "Female (Humanities)",
      dataIndex: ["totals", "humanities_female"],
    },
    {
      title: "Male (Sciences)",
      dataIndex: ["totals", "sciences_male"],
    },
    {
      title: "Female (Sciences)",
      dataIndex: ["totals", "sciences_female"],
    },
    {
      title: "Special Needs",
      dataIndex: ["totals", "special_needs"],
    },
    {
      title: "Total",
      dataIndex: ["totals", "total"],
    },
  ];

  const handleDownloadPDF = () => {
    const title = "Student Statistics Report";
    const fileName = "student-statistics-report.pdf";

    const cols = [
      "Institution / Year / Category",
      "Male (Hum)",
      "Female (Hum)",
      "Male (Sci)",
      "Female (Sci)",
      "Special Needs",
      "Total",
    ];

    const rows = [];

    grouped.forEach((group) => {
      // Group summary (TOTAL)
      rows.push([
        `${group.institution?.name || "N/A"} — ${
          group.session?.name || "N/A"
        } (TOTAL)`,
        group.totals.humanities_male,
        group.totals.humanities_female,
        group.totals.sciences_male,
        group.totals.sciences_female,
        group.totals.special_needs,
        group.totals.total,
      ]);

      // Detail rows by category
      group.data.forEach((item) => {
        const totalItem =
          (item.number_of_males || 0) +
          (item.number_of_females || 0) +
          (item.sciences_number_of_males || 0) +
          (item.sciences_number_of_females || 0);

        rows.push([
          `-> ${item.student_category?.name || "N/A"}`,
          item.number_of_males ?? 0,
          item.number_of_females ?? 0,
          item.sciences_number_of_males ?? 0,
          item.sciences_number_of_females ?? 0,
          item.number_of_students_with_special_needs ?? 0,
          totalItem,
        ]);
      });

      // spacer
      rows.push(["", "", "", "", "", "", ""]);
    });

    generatePDF({ title, columns: cols, rows, fileName });
  };

  const handleDownloadExcel = () => {
    // --- Summary sheet (group totals) ---
    const summaryCols = [
      "Institution",
      "Year",
      "Male (Hum)",
      "Female (Hum)",
      "Male (Sci)",
      "Female (Sci)",
      "Special Needs",
      "Total",
    ];

    const summaryRows = grouped.map((g) => [
      g.institution?.name || "N/A",
      g.session?.name || "N/A",
      g.totals.humanities_male,
      g.totals.humanities_female,
      g.totals.sciences_male,
      g.totals.sciences_female,
      g.totals.special_needs,
      g.totals.total,
    ]);

    // --- Details sheet (each category row) ---
    const detailsCols = [
      "Institution",
      "Year",
      "Student Category",
      "Male (Hum)",
      "Female (Hum)",
      "Male (Sci)",
      "Female (Sci)",
      "Special Needs",
      "Total",
    ];

    const detailsRows = [];
    grouped.forEach((g) => {
      g.data.forEach((item) => {
        const totalItem =
          (item.number_of_males || 0) +
          (item.number_of_females || 0) +
          (item.sciences_number_of_males || 0) +
          (item.sciences_number_of_females || 0);

        detailsRows.push([
          g.institution?.name || "N/A",
          g.session?.name || "N/A",
          item.student_category?.name || "N/A",
          item.number_of_males ?? 0,
          item.number_of_females ?? 0,
          item.sciences_number_of_males ?? 0,
          item.sciences_number_of_females ?? 0,
          item.number_of_students_with_special_needs ?? 0,
          totalItem,
        ]);
      });
    });

    // Build workbook
    const wb = utils.book_new();

    const wsSummary = utils.aoa_to_sheet([summaryCols, ...summaryRows]);
    utils.book_append_sheet(wb, wsSummary, "Summary");

    const wsDetails = utils.aoa_to_sheet([detailsCols, ...detailsRows]);
    utils.book_append_sheet(wb, wsDetails, "Details");

    writeFile(wb, "student-statistics-report.xlsx");
  };

  return (
    <>
      <PageHeading title="Student Statistics Report" subtitle1="Reports" />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Student Statistics Report"
            count={filteredStats.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            searchAdd={false}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={grouped.map((g) => ({
              key: g.key,
              institution: g.institution,
              session: g.session,
              totals: g.totals,
              data: g.data,
            }))}
            expandable={{
              expandedRowKeys: expandedRowKey ? [expandedRowKey] : [],
              onExpand: (expanded, record) => {
                setExpandedRowKey(expanded ? record.key : null);
              },
              expandedRowRender: (record) => (
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="text-left p-2">Student Category</th>
                      <th className="text-left p-2">Male (Hum)</th>
                      <th className="text-left p-2">Female (Hum)</th>
                      <th className="text-left p-2">Male (Sci)</th>
                      <th className="text-left p-2">Female (Sci)</th>
                      <th className="text-left p-2">Special Needs</th>
                      <th className="text-left p-2">Total</th>
                    </tr>
                  </thead>
                  <tbody className="bg-gray-50">
                    {record.data.map((item) => {
                      const totalItem =
                        (item.number_of_males || 0) +
                        (item.number_of_females || 0) +
                        (item.sciences_number_of_males || 0) +
                        (item.sciences_number_of_females || 0);
                      return (
                        <tr key={item.id}>
                          <td className="p-2">
                            {item.student_category?.name || "N/A"}
                          </td>
                          <td className="p-2">{item.number_of_males ?? 0}</td>
                          <td className="p-2">{item.number_of_females ?? 0}</td>
                          <td className="p-2">
                            {item.sciences_number_of_males ?? 0}
                          </td>
                          <td className="p-2">
                            {item.sciences_number_of_females ?? 0}
                          </td>
                          <td className="p-2">
                            {item.number_of_students_with_special_needs ?? 0}
                          </td>
                          <td className="p-2">{totalItem}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ),
            }}
            extra={
              <div className="mb-2 py-2 flex gap-2">
                <Select
                  defaultValue={selectedInstitution}
                  className="w-full h-[36px] min-w-[250px]"
                  onChange={(value) => setSelectedInstitution(value)}
                  showSearch
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  <Option value="Not Selected">Filter by Institution</Option>
                  {institutions.map((inst) => (
                    <Option key={inst.id} value={inst.id}>
                      {inst.name}
                    </Option>
                  ))}
                </Select>

                <Select
                  defaultValue={selectedSession}
                  className="w-full h-[36px] min-w-[250px]"
                  onChange={(value) => setSelectedSession(value)}
                  showSearch
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  <Option value="Not Selected">Filter by Year</Option>
                  {sessions.map((s) => (
                    <Option key={s.id} value={s.id}>
                      {s.name}
                    </Option>
                  ))}
                </Select>
              </div>
            }
          />

          <div className="mt-4 flex gap-2 flex-wrap">
            <DownloadButton
              onDownload={handleDownloadPDF}
              title="Download PDF"
            />
            <DownloadButton
              onDownload={handleDownloadExcel}
              title="Download Excel"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default StudentReport;
